import { format, parseISO } from "date-fns";
import { LoaderIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDateContext } from "@/components/providers/date-provider";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import {
	onlySeenConflicts,
	type RelocationConflict,
	sendJson,
	type TimelineEntry,
} from "@/lib/therapist-relocation";
import { ConflictList } from "./conflict-list";

/**
 * Batalkan an Utama Mulai. Its days go back to the home before it, until the next move or onward.
 * A middle one (a later move follows) needs a reason, and Go checks its visits against that home:
 * the first cancel without acknowledge_conflicts doubles as the check.
 */
export function CancelRelocationDialog({
	entry,
	entries,
	onClose,
	onSaved,
	relocationsPath,
}: {
	/** null keeps the dialog closed */
	entry: TimelineEntry | null;
	entries: TimelineEntry[];
	onClose: () => void;
	/** Reloads what the cancel changed. */
	onSaved: () => void;
	relocationsPath: string;
}) {
	const { t } = useTranslation("therapists", {
		keyPrefix: "relocation.cancel",
	});
	const { locale } = useDateContext();
	const day = (value: string) => format(parseISO(value), "PP", { locale });

	const [reason, setReason] = useState("");
	const [conflicts, setConflicts] = useState<RelocationConflict[]>([]);
	const [error, setError] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);

	// Every opening starts from a blank form.
	useEffect(() => {
		if (!entry) return;
		setReason("");
		setConflicts([]);
		setError(null);
	}, [entry]);

	const at = entry
		? entries.findIndex(
				(other) => other.therapistAddressId === entry.therapistAddressId,
			)
		: -1;
	const previous = at > 0 ? entries[at - 1] : null;
	// Go names a move with a later one after it a middle move; only the last has no end.
	const middle = !!entry?.effectiveUntil;

	const cancel = async () => {
		if (!entry) return;
		if (middle && !reason.trim()) {
			setError(t("errors.reason"));
			return;
		}
		const url = `${relocationsPath}/${entry.therapistAddressId}`;
		setBusy(true);
		setError(null);
		try {
			let result = await sendJson(
				url,
				{ reason, acknowledge_conflicts: false },
				"DELETE",
			);
			if (
				result.status === 409 &&
				result.data &&
				conflicts.length > 0 &&
				onlySeenConflicts(result.data.conflicts, conflicts)
			) {
				result = await sendJson(
					url,
					{ reason, acknowledge_conflicts: true },
					"DELETE",
				);
			}

			if (result.ok) {
				onClose();
				onSaved();
			} else if (result.status === 409 && result.data) {
				setError(
					t(conflicts.length > 0 ? "errors.new_conflicts" : "errors.conflicts"),
				);
				setConflicts(result.data.conflicts);
			} else setError(result.error || t("failed"));
		} catch {
			setError(t("failed"));
		} finally {
			setBusy(false);
		}
	};

	return (
		<AlertDialog
			open={!!entry}
			onOpenChange={(open) => {
				if (!open && !busy) onClose();
			}}
		>
			<AlertDialogContent className="flex max-h-[90dvh] flex-col">
				<AlertDialogHeader>
					<AlertDialogTitle>{t("title")}</AlertDialogTitle>
					<AlertDialogDescription>
						{entry?.effectiveFrom &&
							t("description", {
								city: entry.address.city,
								date: day(entry.effectiveFrom),
							})}{" "}
						{previous &&
							(entry?.effectiveUntil
								? t("extends_until", {
										city: previous.address.city,
										date: day(entry.effectiveUntil),
									})
								: t("extends_onward", { city: previous.address.city }))}
					</AlertDialogDescription>
				</AlertDialogHeader>
				<Textarea
					value={reason}
					disabled={busy}
					onChange={(event) => setReason(event.target.value)}
					placeholder={t(
						middle ? "reason_placeholder_required" : "reason_placeholder",
					)}
					aria-label={t("reason")}
					aria-invalid={middle && !!error && !reason.trim()}
				/>
				{error && (
					<p role="alert" className="text-sm text-destructive">
						{error}
					</p>
				)}
				{conflicts.length > 0 && (
					<section
						aria-live="polite"
						className="grid min-h-0 gap-2 overflow-y-auto"
					>
						<h3 className="text-sm font-semibold">
							{t("conflicts_title", { count: conflicts.length })}
						</h3>
						<ConflictList conflicts={conflicts} />
					</section>
				)}
				<AlertDialogFooter>
					<AlertDialogCancel disabled={busy}>{t("back")}</AlertDialogCancel>
					<AlertDialogAction
						disabled={busy}
						onClick={(event) => {
							// keep the dialog open until the request finishes
							event.preventDefault();
							cancel();
						}}
					>
						{busy && <LoaderIcon className="animate-spin" />}
						{t(conflicts.length > 0 ? "confirm_anyway" : "confirm")}
					</AlertDialogAction>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	);
}
